import { Skeleton } from '@mui/material';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import CustomerDetails from 'src/components/ui/CustomerDetails/CustomerDetails';
import { FormAutocomplete } from 'src/components/ui/FormAutocomplete/FormAutocomplete';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { routes } from 'src/routes';
import {
  getServiceAccountList,
  setServiceAccountList,
} from 'src/store/actions/serviceAccountActions';
import {
  assignTicket,
  getTicketDetails,
  setTicketDetails,
} from 'src/store/actions/ticketActions';
import { ServiceAccount } from 'src/store/models/ServiceAccount';
import { TicketStatus } from 'src/store/models/Ticket';
import { getDistanceFromLatLonInKm } from 'src/utils/addressUtils';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useNonInitialEffect } from 'src/utils/useNonInitialEffect';

import classes from './AssignTicket.module.scss';

export interface AssignTicketFormData {
  assignee: ServiceAccount;
}

const defaultValues: AssignTicketFormData = {
  assignee: null,
};

const AssignTicket: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const { control, handleSubmit, formState } = useForm<AssignTicketFormData>({
    mode: 'onChange',
    defaultValues,
  });

  const loading = useLoadingSelector({
    actionType: getTicketDetails.toString(),
  });
  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const serviceAccountList = useAppSelector(
    (state) => state.serviceAccount.serviceAccountList
  );
  const loadingList = useLoadingSelector({
    actionType: getServiceAccountList.toString(),
  });

  useEffect(() => {
    dispatch(setTicketDetails(null));
    dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    dispatch(getTicketDetails({ id: params?.ticketId }));
    dispatch(getServiceAccountList({ params: { size: 1000 } }));
    return () => {
      dispatch(setTicketDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useNonInitialEffect(() => {
    if (ticket?.status && ticket?.status !== TicketStatus.UNASSIGNED) {
      navigate(routes.TICKETS, { replace: true });
    }
    // eslint-disable-next-line
  }, [ticket]);

  const onSubmit: SubmitHandler<AssignTicketFormData> = (formData) => {
    dispatch(
      assignTicket({ ticketId: ticket?.id, service: formData?.assignee })
    );
  };

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('tickets:assign_ticket')}
        showBackButton
        backConfirmationText={
          formState.isDirty && t('tickets:changes_will_not_save_assign')
        }
        endNode={
          <Button
            type="submit"
            form="assign-ticket-form"
            className={classes.HeaderButton}
            disabled={!formState.isDirty || !ticket}
          >
            {t('tickets:assign_ticket')}
          </Button>
        }
      />
      <h4>{`${t('ticket_details:assign_ticket_to')}:`}</h4>
      <form
        noValidate
        id="assign-ticket-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <section>
          {!loadingList ? (
            <FormAutocomplete<AssignTicketFormData, ServiceAccount>
              name="assignee"
              control={control}
              AutocompleteProps={{
                getOptionLabel: (option) =>
                  `${option?.company?.title} / ${option?.title} / ${t(
                    'tickets:km_away',
                    {
                      number: getDistanceFromLatLonInKm(
                        parseFloat(option?.company?.lat),
                        parseFloat(option?.company?.lon),
                        parseFloat(ticket?.user?.lat),
                        parseFloat(ticket?.user?.lon)
                      ).toFixed(1),
                    }
                  )}`,
                isOptionEqualToValue: (option, value) =>
                  option?.id === value?.id,
                options:
                  serviceAccountList?.data?.filter((s) => s?.enabled) || [],
                label: t('common:assignee'),
                placeholder: `${t('common:choose')}...`,
              }}
            />
          ) : (
            <Skeleton />
          )}
          <p>
            {`${t('ticket_details:cant_find_service')} `}
            <Link to={routes.NEW_SERVICE_ACCOUNT}>
              {t('ticket_details:create_new_service')}
            </Link>
          </p>
        </section>
        <CustomerDetails
          ticket={ticket}
          className={classes.CustomerDetails}
          loading={loading}
        />
      </form>
    </div>
  );
};

export default AssignTicket;
